import { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Typography, Box, Stack, Grid, Divider, Dialog, AppBar, Toolbar, IconButton, Slide, Avatar, Link } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import { useSelector } from 'react-redux';
import axios from 'axios';
import config from 'config';
import { getAuth } from 'firebase/auth';
import ComparisonExampleReport from 'sections/lp-comparer/ComparisonExampleReport';
import React from 'react';
import james from 'assets/images/landing/james.png';
import adobe from 'assets/images/landing/logos/adobe.svg';
import google from 'assets/images/landing/logos/google.png';
import pwc from 'assets/images/landing/logos/pwc.png';

const monthlyPriceId = config.priceIdProUSMonth;
const monthlyPriceIdEU = config.priceIdProEUMonth;

const euCountryCodes = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE'
];

const trialData = {
  trial: true,
  title: 'Try Askpot Pro for FREE for 7 days and get:',
  benefits: [
    'Full competitive analysis',
    'Extended reports: Unique Selling Points, Audience Segmentation, SWOT, Market Overview, Pricing Analysis',
    'Reviews Analysis'
  ],
  buttonText: 'Start Free Trial',
  priceInfo: { trialPeriod: '7 days free', price: '79.00/mo', unsubscribe: 'Unsubscribe anytime.' },
  priceId: monthlyPriceId,
  euPriceId: monthlyPriceIdEU
};

const proData = {
  trial: false,
  title: 'Upgrade to Askpot Pro to run analysis and get:',
  benefits: [
    'Full competitive analysis',
    'Extended reports: Unique Selling Points, Audience Segmentation, SWOT, Market Overview, Pricing Analysis',
    'Export to CSV, MS Word',
    '300 credits/month'
  ],
  buttonText: 'Upgrade Now',
  priceInfo: { price: '79.00/month', unsubscribe: 'Unsubscribe anytime' },
  priceId: monthlyPriceId,
  euPriceId: monthlyPriceIdEU
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const CompetitorCompareModal = ({ open, handleClose }) => {
  const trialWasActivated = useSelector((state) => state.proAccountReducer.trial_was_activated);
  const blockData = !trialWasActivated ? trialData : proData;
  const [isInEurope, setIsInEurope] = useState(null);
  const [trialIsLoading, setTrialIsLoading] = useState(false);
  const [showStickyFooter, setShowStickyFooter] = useState(false);
  const mainCtaRef = useRef(null);
  const reportRef = useRef(null);
  const scrollContainerRef = useRef(null);

  const getCountry = useCallback(() => {
    return fetch('https://ipinfo.io/json?token=4f1c89b6de8103')
      .then((response) => response.json())
      .then((jsonResponse) => euCountryCodes.includes(jsonResponse.country))
      .catch(() => null);
  }, []);

  useEffect(() => {
    getCountry().then(setIsInEurope);
  }, [getCountry]);

  useEffect(() => {
    if (open) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          setShowStickyFooter(!entry.isIntersecting);
        },
        { root: scrollContainerRef.current, threshold: 0.5 }
      );

      const currentRef = mainCtaRef.current;
      if (currentRef) {
        observer.observe(currentRef);
      }

      return () => {
        if (currentRef) {
          observer.unobserve(currentRef);
        }
      };
    }
  }, [open, mainCtaRef, scrollContainerRef]);

  const currency = isInEurope ? '€' : '$';
  const auth = getAuth();
  const apiUrl = config.apiUrl;

  const handlePlanSelectButton = useCallback(
    async (event) => {
      event.preventDefault();
      setTrialIsLoading(true);

      const user = auth.currentUser;
      if (!user) {
        console.error('No authenticated user found');
        setTrialIsLoading(false);
        return;
      }

      // GTM & Yandex Metrika tracking
      if (window.gtag) {
        window.gtag('event', 'conversion', { send_to: 'AW-16481265161/uSXlCMCs_9YZEInM8LI9', value: 39.0, currency: 'EUR' });
      }
      if (window.ym) {
        window.ym(94496474, 'reachGoal', 'START_TRIAL_CLiCK');
      }

      try {
        const token = await user.getIdToken();
        const priceId = isInEurope ? blockData.euPriceId : blockData.priceId;
        const endpointUrl = `${apiUrl}/pro_plans/create_checkout_session/${priceId}?trial_days=${blockData.trial}`;

        const response = await axios.get(endpointUrl, { headers: { Authorization: `Bearer ${token}` } });
        const data = response.data;

        if (data.url && response.status === 200) {
          window.location.href = data.url;
        } else {
          console.error('Draft subscription creation failed:', data.error);
        }
      } catch (error) {
        console.error('Error creating draft subscription:', error);
      } finally {
        setTrialIsLoading(false);
      }
    },
    [auth, apiUrl, isInEurope, blockData]
  );

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start'
        }
      }}
    >
      <AppBar
        position="static"
        color="inherit"
        sx={{
          flexShrink: 0,
          bgcolor: 'background.paper',
          boxShadow: 'none',
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                bgcolor: 'primary.main',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <StarIcon sx={{ color: 'common.white', fontSize: 20 }} />
            </Box>
            <Typography variant="h4" component="div">
              {!trialWasActivated ? 'Start Free Trial to Compare Competitors' : 'Upgrade to Compare Competitors'}
            </Typography>
          </Box>
          <IconButton edge="end" color="inherit" onClick={handleClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Box ref={scrollContainerRef} sx={{ overflowY: 'auto', flexGrow: 1 }}>
        <Box sx={{ p: 3 }}>
          <Grid container>
            <Grid item xs={12} md={6} sx={{ p: 4, mb: 3 }}>
              <Typography variant="h2" sx={{ mb: 2, lineHeight: 1.2, fontWeight: 'bold' }}>
                Unlock powerful competitive analysis
              </Typography>
              <Typography variant="h5" color="text.secondary" sx={{ mb: 4 }}>
                Get comprehensive insights and advanced reporting tools to stay ahead of your competition.
              </Typography>

              <Box sx={{ my: 3 }}>
                {blockData.benefits.map((benefit, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <CheckCircleIcon fontSize="small" color="success" sx={{ mr: 1.5 }} />
                    <Typography variant="body1">{benefit}</Typography>
                  </Box>
                ))}
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box ref={mainCtaRef} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'start' }}>
                <Button
                  disabled={trialIsLoading}
                  variant="contained"
                  size="large"
                  onClick={handlePlanSelectButton}
                  sx={{
                    background: 'linear-gradient(90deg, #3b82f6 0%, #34d399 100%)',
                    '&:hover': { background: 'linear-gradient(90deg, #2563eb 0%, #10b981 100%)' },
                    boxShadow: 'none',
                    py: 1.5,
                    px: 3
                  }}
                >
                  {trialIsLoading ? 'Redirecting...' : blockData.buttonText}
                </Button>
                <Stack direction="column" ml={2}>
                  {blockData.priceInfo.trialPeriod && (
                    <Typography variant="body1" color="textPrimary">
                      <b>{blockData.priceInfo.trialPeriod}</b>, then {currency}
                      {blockData.priceInfo.price}
                    </Typography>
                  )}
                  {!blockData.priceInfo.trialPeriod && (
                    <Typography variant="body1" color="textPrimary">
                      {currency}
                      {blockData.priceInfo.price}
                    </Typography>
                  )}
                  <Typography variant="body1" color="textPrimary">
                    {blockData.priceInfo.unsubscribe}
                  </Typography>
                </Stack>
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
                By continuing checkout, you agree to the{' '}
                <Typography component="a" variant="body2" target="_blank" href="/maintenance/terms" sx={{ color: 'text.secondary' }}>
                  Terms of Use
                </Typography>{' '}
                and{' '}
                <Typography component="a" variant="body2" target="_blank" href="/maintenance/cancellation" sx={{ color: 'text.secondary' }}>
                  Cancellation Policy
                </Typography>
              </Typography>
            </Grid>
            <Grid item xs={12} md={6} sx={{ p: 4, mb: 3, borderLeft: { md: '1px solid' }, borderColor: { md: 'divider' } }}>
              <Box sx={{ p: { xs: 2, sm: 4 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar alt="James Clampett" src={james} sx={{ width: 56, height: 56 }} />
                  <Box sx={{ ml: 2 }}>
                    <Link
                      href="https://www.linkedin.com/feed/update/urn:li:activity:7339056292780568577/?commentUrn=urn%3Ali%3Acomment%3A(activity%3A7339056292780568577%2C7339060907177644032)&dashCommentUrn=urn%3Ali%3Afsd_comment%3A(7339060907177644032%2Curn%3Ali%3Aactivity%3A7339056292780568577)"
                      target="_blank"
                      sx={{ textDecoration: 'none', color: 'inherit' }}
                    >
                      <Typography variant="subtitle1">James Clampett</Typography>
                    </Link>
                    <Typography variant="subtitle2">Principal | insight & foresight | Futures focused</Typography>
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ mt: 2 }}>
                  &ldquo;Askpot has proven super insightful to identify risks and opportunities, gaps in the market and new positioning
                  ideas for my business. It&apos;s amazing to have tools like this that can create insightful data sets in minutes and only
                  further proof of how fast everything is changing.&rdquo;
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  TRUSTED BY PROFESSIONALS AT
                </Typography>
                <Stack
                  direction="row"
                  spacing={4}
                  justifyContent="center"
                  alignItems="center"
                  sx={{ filter: 'grayscale(100%)', opacity: 0.6 }}
                >
                  <img src={adobe} alt="Adobe" style={{ height: '24px' }} />
                  <img src={google} alt="Google" style={{ height: '24px' }} />
                  <img src={pwc} alt="PwC" style={{ height: '24px' }} />
                </Stack>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0 }}>
                <Button variant="outlined" onClick={() => reportRef.current.scrollIntoView({ behavior: 'smooth' })}>
                  View Sample Report
                </Button>
              </Box>
            </Grid>
            <Grid ref={reportRef} item xs={12} sx={{ p: 2, bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h2" sx={{ mb: 2, ml: 1 }}>
                Sample Report Preview
              </Typography>
              <ComparisonExampleReport />
            </Grid>
          </Grid>
        </Box>
      </Box>
      <Slide direction="up" in={showStickyFooter} mountOnEnter unmountOnExit>
        <AppBar
          position="static"
          color="inherit"
          sx={{ flexShrink: 0, top: 'auto', bottom: 0, borderTop: '1px solid', borderColor: 'divider' }}
        >
          <Toolbar>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', p: 1, gap: 3 }}>
              <Stack direction="column">
                <Typography variant="h6" color="textPrimary">
                  {blockData.priceInfo.trialPeriod ? (
                    <>
                      <b>{blockData.priceInfo.trialPeriod}</b>, then {currency}
                      {blockData.priceInfo.price}
                    </>
                  ) : (
                    <>
                      {currency}
                      {blockData.priceInfo.price}
                    </>
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {blockData.priceInfo.unsubscribe}
                </Typography>
              </Stack>

              <Button
                disabled={trialIsLoading}
                variant="contained"
                size="large"
                onClick={handlePlanSelectButton}
                sx={{
                  background: 'linear-gradient(90deg, #3b82f6 0%, #34d399 100%)',
                  '&:hover': { background: 'linear-gradient(90deg, #2563eb 0%, #10b981 100%)' },
                  boxShadow: 'none',
                  py: 1.5,
                  px: 3
                }}
              >
                {trialIsLoading ? 'Redirecting...' : blockData.buttonText}
              </Button>
            </Box>
          </Toolbar>
        </AppBar>
      </Slide>
    </Dialog>
  );
};

export default CompetitorCompareModal;
